/**
 * Custom event object for listeners.
 *
 * Inspired by jQuery, has the correct `currentTarget` prop set when using
 * delegation. Also makes it possible to normalize other things like keyboard
 * event `key`.
 */
export default class ListenerEvent {
  constructor(srcEvent, currentTarget = null) {
    this.originalEvent = srcEvent;

    // Targets
    // Custom currentTarget if passed
    this.currentTarget =
      currentTarget ||
      // Target should not be a text node
      (srcEvent.currentTarget && srcEvent.currentTarget.nodeType === 3
        ? srcEvent.currentTarget.parentNode
        : srcEvent.currentTarget);
    this.target = srcEvent.target;
    this.relatedTarget = srcEvent.relatedTarget;

    // Meta data
    // Create a timestamp if incoming event doesn't have one
    this.timeStamp = (srcEvent && srcEvent.timeStamp) || Date.now();

    // Key values
    // https://developer.mozilla.org/sv-SE/docs/Web/API/KeyboardEvent/key/Key_Values
    // Some incorrect values like Del and Esc show in the above docs as problems
    // in IE9, but testing shows it's still wrong in IE11. A bunch of special
    // keys like CrSel/ExSel (?) and media buttons are not fixed here since
    // they will probably never be used.
    switch (srcEvent.key) {
      case 'Spacebar':
        this.key = ' ';
        break;

      case 'Esc':
        this.key = 'Escape';
        break;

      case 'Del':
        this.key = 'Delete';
        break;

      case 'Left':
      case 'Right':
      case 'Up':
      case 'Down':
        this.key = `Arrow${srcEvent.key}`;
        break;

      default:
        this.key = srcEvent.key;
        break;
    }

    // Copy props as-is
    const copyProps = [
      'altKey',
      'bubbles',
      'button',
      'buttons',
      'cancelable',
      'changedTouches',
      'char',
      'charCode',
      'clientX',
      'clientY',
      'code',
      'ctrlKey',
      'detail',
      'eventPhase',
      'keyCode',
      'metaKey',
      'offsetX',
      'offsetY',
      'pageX',
      'pageY',
      'pointerId',
      'pointerType',
      'screenX',
      'screenY',
      'shiftKey',
      'srcElement',
      'state',
      'targetTouches',
      'toElement',
      'touches',
      'type',
      'view',
    ];
    copyProps.forEach((propName) => {
      this[propName] = srcEvent[propName];
    });
  }

  preventDefault() {
    this.originalEvent.preventDefault();
  }

  stopPropagation() {
    this.originalEvent.stopPropagation();
  }

  stopImmediatePropagation() {
    this.originalEvent.stopImmediatePropagation();
  }
}
